import _ from 'lodash'
import * as React from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useFormikContext } from 'formik'
import { Autocomplete as AutocompleteMUI } from '@mui/material'
import Textfield from '../FormsUI/Textfield'
import * as service from '../../services'
import {
  saveTemporaryOptionsSpecie,
  setEnableSpecieField,
} from '../../redux/slices/Tree/treeFeatures'

const MSJ_ERROR = { error: true, helperText: 'Obligatorio' }

const InputGroup1 = ({ name, options, editable, ...rest }) => {
  const dispatch = useDispatch()
  const { values, setFieldValue } = useFormikContext()
  const valuesOptions = Object.values(options)
  const sortedOptionsList = valuesOptions.sort()
  const tOptionsSpecie = useSelector(state => state.treeFeatures.temporaryOptionsSpecie)
  const specieField = useSelector(
    state => state.treeFeatures.enableSaveButton.specieField
  )

  const optionsListInitial = React.useMemo(
    () =>
      _.map(sortedOptionsList, label => ({
        id: _.findKey(options, val => val === label),
        name,
        label: label,
      })),
    [sortedOptionsList, options, name],
  )

  const lastItem = _.remove(optionsListInitial, obj => obj.id === '#')
  if (Boolean(lastItem.length)) optionsListInitial.push(lastItem[0])

  const initialSelectedList = React.useMemo(
    () =>
      Boolean(rest.initvalue)
        ? _.find(optionsListInitial, ['id', `${rest.initvalue}`])
        : null,
    [rest.initvalue, optionsListInitial],
  )
  const [filterList1, setSelectedList] = React.useState(initialSelectedList)

  const optionsList = name === 'family_' ? optionsListInitial : (tOptionsSpecie || [])

  function isInOptions() {
    return tOptionsSpecie ? _.some(tOptionsSpecie, ['id', filterList1.id]) : true
  }

  const othersProps = {
    name,
    value: filterList1?.id ?? '',
    label: rest.label,
    variant: 'standard',
    error: name === 'specie_' ? specieField.error : undefined,
    helperText: name === 'specie_' ? specieField.helperText : undefined,
  }

  const handleSelection = async (event, newList) => {
    setSelectedList(newList)
    const nextId = newList?.id ?? ''
    if (name === 'family_') {
      if (nextId !== values.family_) {
        setFieldValue('specie_', '')
      }
      setFieldValue('family_', nextId)
      let opts = null
      if (Boolean(nextId)) {
        const familyId = nextId
        const response = await service.getSpeciesByFamilyId(familyId)
        opts = _.map(response, o => ({
          id: o?.id_e?.toString(),
          name,
          label: o?.nombre_cientifico,
        }))
      }
      dispatch(saveTemporaryOptionsSpecie(opts))
    } else {
      setFieldValue('specie_', nextId)
    }
  }

  const handleOnBlur = () => {
    if (name === 'specie_')
      if (!specieField.error && !Boolean(filterList1?.label?.length))
        dispatch(setEnableSpecieField(MSJ_ERROR))
  }

  const getOptionDisabled = options => options?.id === '#'

  React.useEffect(() => {
    if (name !== 'specie_') return
    let validatorInput = { error: false, selected: filterList1 }
    if (!Boolean(filterList1)) validatorInput = MSJ_ERROR
    else if (!isInOptions()) {
      // Solo limpiar si el form value también quedó sin especie
      // (evita loop cuando tOptionsSpecie tiene datos de otro árbol aún no recargados)
      const formValStr = `${values?.[name] ?? ''}`
      if (filterList1?.id !== formValStr) setSelectedList(null)
    }
    if (tOptionsSpecie || Boolean(filterList1))
      dispatch(setEnableSpecieField(validatorInput))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterList1, tOptionsSpecie])

  React.useEffect(() => {
    if (name !== 'specie_') return
    if (values?.family_) {
      // Siempre recargar al cambiar la familia (ignora tOptionsSpecie obsoleto de otro árbol)
      service.getSpeciesByFamilyId(values.family_).then(response => {
        const opts = _.map(response, o => ({
          id: o?.id_e?.toString(),
          name,
          label: o?.nombre_cientifico,
        }))
        dispatch(saveTemporaryOptionsSpecie(opts))
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, values?.family_])

  React.useEffect(() => {
    const formValue = values?.[name]
    if (!formValue) {
      if (filterList1) setSelectedList(null)
      return
    }
    const formValueStr = `${formValue}`
    if (filterList1?.id === formValueStr) return
    const fromSpecies = tOptionsSpecie?.find(opt => `${opt.id}` === formValueStr)
    const fromAll = optionsListInitial.find(opt => `${opt.id}` === formValueStr)
    setSelectedList(fromSpecies || fromAll || { id: formValueStr, name, label: formValueStr })
  }, [values?.[name], name, tOptionsSpecie, optionsListInitial, filterList1])

  React.useEffect(() => {
    return () => {
      if (name === 'specie_') {
        // Siempre limpiar al desmontar para evitar que datos de un árbol
        // contaminen la siguiente carga (causa del loop con tOptionsSpecie obsoleto)
        dispatch(setEnableSpecieField({ error: false, selected: null }))
        dispatch(saveTemporaryOptionsSpecie(null))
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name])

  return (
    <AutocompleteMUI
      id={`update-${name}`}
      name={name}
      value={filterList1}
      onChange={handleSelection}
      onBlur={handleOnBlur}
      options={optionsList ?? optionsListInitial}
      noOptionsText="Opción no válida"
      readOnly={!editable}
      getOptionLabel={option => option?.label}
      getOptionDisabled={getOptionDisabled}
      isOptionEqualToValue={(option, value) => {
        if (option.id === value.id) return true
        // Suppress warning during transitional render when a new family is selected
        // and the previously-selected species hasn't been reset by the useEffect yet.
        if (name === 'specie_') {
          const currentOptions = optionsList ?? optionsListInitial
          return !currentOptions.some(o => o.id === value.id)
        }
        return false
      }}
      renderInput={params => <Textfield {...params} {...othersProps} />}
    />
  )
}

export default React.memo(InputGroup1)
